import { motion } from "framer-motion";
import {
  UserPlus,
  Flame,
  CheckCircle2,
  CalendarClock,
  MessageCircle,
  Bot,
  LayoutTemplate,
  Rocket,
  Users,
  BellRing,
  History,
} from "lucide-react";
import { LANDING } from "@/constants/landingTestIds";

const WHATSAPP_PHONE_URL =
  "https://static.prod-images.emergentagent.com/jobs/6a95338f-c56f-4ee5-a4c6-ff921ff7ecc5/images/d9272de237e699bc086536d17333137fdfdcdcd0d4dae1a0460b201190a0fcaa.png";
const BUILDER_PHONE_URL =
  "https://static.prod-images.emergentagent.com/jobs/6a95338f-c56f-4ee5-a4c6-ff921ff7ecc5/images/d8949e9de6702bf65b7a735b9d536837d0d391fda862f1a17e3bbb98360ef543.png";

const PIPELINE_STAGES = [
  { label: "New Lead", count: 18, icon: UserPlus, color: "bg-blue-50 text-blue-600 border-blue-100" },
  { label: "Interested", count: 12, icon: Flame, color: "bg-amber-50 text-amber-600 border-amber-100" },
  { label: "Converted", count: 7, icon: CheckCircle2, color: "bg-emerald-50 text-emerald-600 border-emerald-100" },
  { label: "Bookings", count: 4, icon: Rocket, color: "bg-violet-50 text-violet-600 border-violet-100" },
  { label: "Today's Site Visits", count: 3, icon: CalendarClock, color: "bg-teal-50 text-teal-600 border-teal-100" },
];

const cardMotion = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: "easeOut" } },
};

const FeaturesSection = () => {
  return (
    <section id="features" className="bg-white py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#2563EB]">
            Core Features
          </span>
          <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-bold tracking-tight text-[#0F172A]">
            Everything you need, built into one app
          </h2>
          <p className="mt-5 text-base sm:text-lg text-slate-500 leading-relaxed">
            From lead capture to conversion, Tracre One automates the busywork so you can
            focus on closing deals.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          {/* Visual Pipeline Management - large card */}
          <motion.div
            variants={cardMotion}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            data-testid={LANDING.featurePipelineCard}
            className="lg:col-span-8 rounded-2xl border border-slate-100 bg-white p-8 sm:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_40px_rgb(37,99,235,0.10)] transition-shadow duration-300"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-11 h-11 rounded-xl bg-[#2563EB]/10 flex items-center justify-center">
                <LayoutTemplate className="w-5 h-5 text-[#2563EB]" />
              </div>
              <h3 className="font-heading text-2xl font-semibold text-[#0F172A]">
                Visual Pipeline Management
              </h3>
            </div>
            <p className="text-slate-500 mb-8 max-w-xl">
              See every stage of your sales funnel at a glance — from New Lead to Bookings
              — and never lose track of today's site visits again.
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {PIPELINE_STAGES.map((stage) => (
                <div
                  key={stage.label}
                  className={`rounded-xl border p-4 flex flex-col gap-3 hover:-translate-y-1 transition-transform duration-300 ${stage.color}`}
                >
                  <stage.icon className="w-5 h-5" />
                  <div>
                    <p className="text-2xl font-bold font-heading">{stage.count}</p>
                    <p className="text-xs font-medium leading-tight mt-1">{stage.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* AI WhatsApp Automation - tall card */}
          <motion.div
            variants={cardMotion}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            data-testid={LANDING.featureWhatsappCard}
            className="lg:col-span-4 lg:row-span-2 rounded-2xl border border-slate-100 bg-[#0F172A] p-8 sm:p-10 shadow-xl relative overflow-hidden flex flex-col"
          >
            <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-[#14b8a6]/20 rounded-full blur-[90px]" />
            <div className="relative w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center mb-5">
              <MessageCircle className="w-5 h-5 text-[#2dd4bf]" />
            </div>
            <h3 className="relative font-heading text-2xl font-semibold text-white mb-2">
              AI-Powered WhatsApp Automation
            </h3>
            <p className="relative text-slate-300 mb-6">
              Smart lead nurturing and automated follow-ups powered by AI — so no
              lead ever goes cold.
            </p>
            <div className="relative flex items-center gap-2 text-sm text-[#2dd4bf] font-medium mb-6">
              <Bot className="w-4 h-4" /> AI Co-pilot Included
            </div>
            <div className="relative mt-auto flex justify-center">
              <img
                src={WHATSAPP_PHONE_URL}
                alt="AI WhatsApp automation on Tracre One"
                className="w-40 sm:w-48 rounded-2xl shadow-2xl ring-1 ring-white/10"
              />
            </div>
          </motion.div>

          {/* Instant Landing Page Builder - square card */}
          <motion.div
            variants={cardMotion}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            data-testid={LANDING.featureBuilderCard}
            className="lg:col-span-4 rounded-2xl border border-slate-100 bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_40px_rgb(37,99,235,0.10)] transition-shadow duration-300 flex gap-6 items-center"
          >
            <div className="flex-1">
              <div className="w-11 h-11 rounded-xl bg-[#2563EB]/10 flex items-center justify-center mb-4">
                <Rocket className="w-5 h-5 text-[#2563EB]" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-[#0F172A] mb-2">
                Instant Landing Page Builder
              </h3>
              <p className="text-slate-500 text-sm">
                Create high-converting property landing pages in seconds, right from your
                phone.
              </p>
            </div>
            <img
              src={BUILDER_PHONE_URL}
              alt="Instant landing page builder on Tracre One"
              className="w-20 sm:w-24 shrink-0 rounded-xl shadow-lg ring-1 ring-slate-200"
            />
          </motion.div>

          {/* Lead Tracking & Nurturing - square card */}
          <motion.div
            variants={cardMotion}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            data-testid={LANDING.featureTrackingCard}
            className="lg:col-span-4 rounded-2xl border border-slate-100 bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_40px_rgb(37,99,235,0.10)] transition-shadow duration-300"
          >
            <div className="w-11 h-11 rounded-xl bg-[#2563EB]/10 flex items-center justify-center mb-4">
              <Users className="w-5 h-5 text-[#2563EB]" />
            </div>
            <h3 className="font-heading text-xl font-semibold text-[#0F172A] mb-2">
              Lead Tracking & Nurturing
            </h3>
            <p className="text-slate-500 text-sm mb-5">
              A centralized dashboard to capture and track every interaction across your
              pipeline.
            </p>
            <div className="flex items-center gap-4 text-slate-400">
              <div className="flex items-center gap-1.5 text-xs font-medium">
                <BellRing className="w-3.5 h-3.5" /> Reminders
              </div>
              <div className="flex items-center gap-1.5 text-xs font-medium">
                <History className="w-3.5 h-3.5" /> Full History
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
